/** Imports ---------------------------------------------------------------- */

/** React imports */
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import PropTypes from "prop-types";

/** Icons */
import { ReactComponent as CloudUploadIcon } from "../../../assets/images/CloudUploadIcon.svg";
import { ReactComponent as InfoIcon } from "../../../assets/images/info-icon.svg";

/** MUI Icons */
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

/** MUI imports */
import { styled } from "@mui/material/styles";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    LinearProgress,
    List,
    ListSubheader,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    DialogTitle,
    ToggleButtonGroup,
    ToggleButton,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TablePagination,
    Paper,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

/** Component imports */
import Backdrop from "../Backdrop";
import moment from "moment";
import DialogAlerts from "../DialogAlerts";

const PREFIX = "UploadAsync";

const classes = {
    dropzone: `${PREFIX}-dropzone`,
    dropzoneDisabled: `${PREFIX}-dropzoneDisabled`,
};

const CustomFormControlLabel = styled(FormControlLabel)(() => ({
    "& .MuiFormControlLabel-label": {
        fontWeight: "bold",
    },
}));

const CustomDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(4),
    },
    "& .MuiDialogTitle-root": {
        backgroundColor: "#031851",
        fontSize: "18px",
        color: "#fff",
    },
    "& .MuiDialog-paper": {
        borderRadius: 8,
        boxShadow: "10px 10px 15px rgba(3,24,81,0.15)",
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2),
    },

    [`& .${classes.dropzone}`]: {
        flex: 1,
        display: "flex",
        padding: "64px 24px",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "rgba(47,49,144,0.1)",
        border: "2px dashed #C1C1DE",
        borderRadius: "16px",
        "&:hover": {
            border: "2px dashed #2f3190",
        },
    },
    [`& .${classes.dropzoneDisabled}`]: {
        flex: 1,
        display: "flex",
        padding: "64px 24px",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "rgba(47,49,144,0.1)",
        border: "2px dashed #C1C1DE",
        borderRadius: "16px",
    },
}));

const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "rgba(19, 31, 62, 0.80)",
        fontSize: 12,
        maxWidth: 320,
        borderRadius: 6,
        fontWeight: "normal",
    },
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    width: "100%",
    "& .MuiToggleButtonGroup-grouped": {
        border: 0,
        borderRadius: 30,
        height: 35,
        width: "50%",
        backgroundColor: "rgba(47,49,144,0.1)",
        color: "#031851",
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.05)",
        },
    },
    "& .Mui-selected": {
        backgroundColor: "#2f3190 !important",
        color: "white !important",
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.8) !important",
        },
    },
}));

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: "flex", mt: 2, alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress
                    variant="determinate"
                    {...props}
                    sx={{
                        height: 10,
                        borderRadius: 5,
                    }}
                />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}

LinearProgressWithLabel.propTypes = {
    /**
     * The value of the progress indicator for the determinate and buffer variants.
     * Value between 0 and 100.
     */
    value: PropTypes.number.isRequired,
};

const UploadAsync = (props) => {
    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /* React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [showBackdrop, setBackDrop] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressChecks, setProgressChecks] = useState(0);
    const [progressPetty, setProgressPetty] = useState(0);
    const [openAlertDelete, setOpenAlertDelete] = useState(false);
    const rowsPerPage = 10;
    //.pdf, .csv, .txt, .xlsx, .xls.
    let acceptFormat = {};
    if (props.fileFormat && props.fileFormat.includes("pdf")) {
        acceptFormat["application/pdf"] = [];
    }
    if (props.fileFormat && props.fileFormat.includes("csv")) {
        acceptFormat["text/csv"] = [];
    }
    if (props.fileFormat && props.fileFormat.includes("txt")) {
        acceptFormat["text/plain"] = [];
    }
    if (props.fileFormat && props.fileFormat.includes("xlsx")) {
        acceptFormat["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"] = [];
    }
    if (props.fileFormat && props.fileFormat.includes("xls")) {
        acceptFormat["application/vnd.ms-excel"] = [];
    }

    const handleChangePage = (event, newPage) => {
        props.setPage(newPage);
    };

    const meses = [
        t("monthNames.january"),
        t("monthNames.february"),
        t("monthNames.march"),
        t("monthNames.april"),
        t("monthNames.may"),
        t("monthNames.june"),
        t("monthNames.july"),
        t("monthNames.august"),
        t("monthNames.september"),
        t("monthNames.october"),
        t("monthNames.november"),
        t("monthNames.december"),
    ];

    /** Global variables */
    const teamDelete = [
        "kgarcia@gca.digital",
        "dloyo@gca.digital",
        "gloyo@gca.digital",
        "dleon@gca.digital",
        "iguevara@gca.digital",
        "galbani@gca.digital",
        "btorres@gca.digital",
        "jrojas@gca.digital",
        "jalvarez@gca.digital",
        "mmedina@gca.digital",
    ];

    /** Component functions */
    const formatBytes = (bytes, decimals = 0) => {
        if (bytes === 0) return "0 Bytes";

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    const onDrop = useCallback(
        async (acceptedFiles) => {
            if (acceptedFiles[0].size < 5000000) {
                let data = new FormData();
                data.append("id_organisation", props.selectedBank.idOrganisation);
                data.append("bank_id", props.selectedBank.idAccount);
                data.append("month", props.selectedBank.month);
                data.append("year", props.selectedYear);
                data.append("file", acceptedFiles[0]);
                data.append("folder", "Bancos");

                const options = {
                    onUploadProgress: (progressEvent) => {
                        const { loaded, total } = progressEvent;
                        let percent = Math.floor((loaded / total) * 100);

                        if (percent < 100) {
                            setProgress(percent);
                        }
                    },
                };

                await axios
                    .post("/uploadFileS3", data, options)
                    .then(() => {
                        setProgress(0);
                        props.setFilesData([...acceptedFiles]);
                        props.setFilesRejected([]);
                        if (props.selectedBank.mode !== undefined && props.selectedBank.mode !== 0) {
                            props.setDialogAction("Convertir");
                            props.setDialogText(t("bank.convertBankStatement"));
                        } else {
                            props.setDialogAction("Guardar");
                            props.setDialogText(t("bank.saveBankStatement"));
                        }
                        props.loadInitData();
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        },
        [props, t]
    );

    const onDropChecks = useCallback(
        async (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                let params = {
                    id_organisation: props.selectedBank.idOrganisation,
                    bank_id: props.selectedBank.idAccount,
                    month: props.selectedBank.month,
                    year: props.selectedYear,
                    folder: "Cheques",
                };
                await axios
                    .get("/getTotalSizeFileS3", { params })
                    .then(async (response) => {
                        let finalSize = 0;
                        acceptedFiles.forEach((item) => {
                            finalSize += item.size;
                        });

                        if (parseInt(response.data.size + finalSize) < 10000000) {
                            let data = new FormData();
                            data.append("id_organisation", props.selectedBank.idOrganisation);
                            data.append("bank_id", props.selectedBank.idAccount);
                            data.append("month", props.selectedBank.month);
                            data.append("year", props.selectedYear);
                            data.append("folder", "Cheques");
                            if (acceptedFiles.length > 1) {
                                for (var x = 0; x < acceptedFiles.length; x++) {
                                    data.append("files", acceptedFiles[x]);
                                }
                            } else {
                                data.append("file", acceptedFiles[0]);
                            }

                            const options = {
                                onUploadProgress: (progressEvent) => {
                                    const { loaded, total } = progressEvent;
                                    let percent = Math.floor((loaded / total) * 100);

                                    if (percent < 100) {
                                        setProgressChecks(percent);
                                    }
                                },
                            };
                            if (acceptedFiles.length > 1) {
                                await axios
                                    .post("/uploadFileS3multiple", data, options)
                                    .then(() => {
                                        setProgressChecks(0);
                                        props.setFilesDataChecks(props.filesDataChecks.concat([...acceptedFiles]));
                                        props.setFilesRejectedChecks([]);
                                        props.loadInitData();
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                    });
                            } else {
                                await axios
                                    .post("/uploadFileS3", data, options)
                                    .then(() => {
                                        setProgressChecks(0);
                                        props.setFilesDataChecks(props.filesDataChecks.concat([...acceptedFiles]));
                                        props.setFilesRejectedChecks([]);
                                        props.loadInitData();
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                    });
                            }
                        } else {
                            props.setTexto(t("dialogConversions.maximunUploadSizeMonthly") + " 10 MB.");
                            props.setAlertType("warning");
                            props.setShow(true);
                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        },
        [props, t]
    );

    const onDropPetty = useCallback(
        async (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                let params = {
                    id_organisation: props.selectedBank.idOrganisation,
                    bank_id: props.selectedBank.idAccount,
                    month: props.selectedBank.month,
                    year: props.selectedYear,
                    folder: "CajasChicas",
                };
                await axios
                    .get("/getTotalSizeFileS3", { params })
                    .then(async (response) => {
                        let finalSize = 0;
                        acceptedFiles.forEach((item) => {
                            finalSize += item.size;
                        });

                        if (parseInt(response.data.size + finalSize) < 5000000) {
                            let data = new FormData();
                            data.append("id_organisation", props.selectedBank.idOrganisation);
                            data.append("bank_id", props.selectedBank.idAccount);
                            data.append("month", props.selectedBank.month);
                            data.append("year", props.selectedYear);
                            data.append("folder", "CajasChicas");
                            if (acceptedFiles.length > 1) {
                                for (var x = 0; x < acceptedFiles.length; x++) {
                                    data.append("files", acceptedFiles[x]);
                                }
                            } else {
                                data.append("file", acceptedFiles[0]);
                            }

                            const options = {
                                onUploadProgress: (progressEvent) => {
                                    const { loaded, total } = progressEvent;
                                    let percent = Math.floor((loaded / total) * 100);

                                    if (percent < 100) {
                                        setProgressPetty(percent);
                                    }
                                },
                            };

                            if (acceptedFiles.length > 1) {
                                await axios
                                    .post("/uploadFileS3multiple", data, options)
                                    .then(() => {
                                        setProgressPetty(0);
                                        props.setFilesDataPetty(props.filesDataChecks.concat([...acceptedFiles]));
                                        props.setFilesRejectedPetty([]);
                                        props.loadInitData();
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                    });
                            } else {
                                await axios
                                    .post("/uploadFileS3", data, options)
                                    .then(() => {
                                        setProgressPetty(0);
                                        props.setFilesDataPetty(props.filesDataPetty.concat([...acceptedFiles]));
                                        props.setFilesRejectedPetty([]);
                                        props.loadInitData();
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                    });
                            }
                        } else {
                            props.setTexto(t("dialogConversions.maximunUploadSizeMonthly") + " 5 MB.");
                            props.setAlertType("warning");
                            props.setShow(true);
                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        },
        [props, t]
    );

    const onDropRejected = useCallback(
        (fileRejections) => {
            props.setFilesRejected([...props.filesRejected, ...fileRejections]);
        },
        [props]
    );

    const onDropRejectedChecks = useCallback(
        (fileRejections) => {
            props.setFilesRejectedChecks([...props.filesRejectedChecks, ...fileRejections]);
        },
        [props]
    );

    const onDropRejectedPetty = useCallback(
        (fileRejections) => {
            props.setFilesRejectedPetty([...props.filesRejectedPetty, ...fileRejections]);
        },
        [props]
    );

    const { getRootProps: getRootBanksProps, getInputProps: getInputBanksProps } = useDropzone({
        disabled:
            props.filesData.length > 0 ||
            (props.uploadedFiles.length > 0 &&
                props.uploadedFiles.filter((item) => item.req_type.toString() === props.buttonTabs.toString())[0].files
                    .length > 0) ||
            props.fileFormat.length === 0 ||
            props.checkStatus,
        noKeyboard: true,
        maxSize: 5000000,
        maxFiles: 1,
        multiple: false,
        onDrop,
        onDropRejected,
        accept: acceptFormat,
    });

    const { getRootProps: getRootChecksProps, getInputProps: getInputChecksProps } = useDropzone({
        disabled: props.checkStatusChecks || props.fullLoadChecks,
        noKeyboard: true,
        multiple: true,
        onDrop: onDropChecks,
        onDropRejected: onDropRejectedChecks,
        accept: {
            "image/*": ['.jpeg', '.jpg', '.png'],
            "application/pdf": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
            "application/zip": ['.zip'],
        },
    });

    const { getRootProps: getRootPettyProps, getInputProps: getInputPettyProps } = useDropzone({
        disabled: props.checkStatusPetty,
        noKeyboard: true,
        multiple: true,
        onDrop: onDropPetty,
        onDropRejected: onDropRejectedPetty,
        accept: {
            "application/pdf": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
        },
    });

    const removeFile = (file, folder) => async () => {
        setBackDrop(true);
        let params = {};
        let tabText =
            props.buttonTabs === 1
                ? "Estados de cuenta"
                : props.buttonTabs === 2
                    ? "Cheques"
                    : props.buttonTabs === 3
                        ? "Reporte de cajas chicas"
                        : "";

        if (
            props.uploadedFiles.filter((item) => item.req_type.toString() === props.buttonTabs.toString())[0].files
                .length === 0
        ) {
            params = {
                id_organisation: props.selectedBank.idOrganisation,
                bank_id: props.selectedBank.idAccount,
                month: props.selectedBank.month,
                year: props.selectedYear,
                file_name: file.name,
                folder: folder,
                tabText,
            };

            await axios
                .post("/deleteFileS3", params)
                .then(() => {
                    if (folder === "Bancos") {
                        const newFiles = [...props.filesData];
                        newFiles.splice(newFiles.indexOf(file), 1);
                        props.setFilesData(newFiles);
                    } else if (folder === "Cheques") {
                        const newFiles = [...props.filesDataChecks];
                        newFiles.splice(newFiles.indexOf(file), 1);
                        props.setFilesDataChecks(newFiles);
                    } else {
                        const newFiles = [...props.filesDataPetty];
                        newFiles.splice(newFiles.indexOf(file), 1);
                        props.setFilesDataPetty(newFiles);
                    }
                    props.loadInitData();
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            params = {
                id_organisation: props.selectedBank.idOrganisation,
                bank_id: props.selectedBank.idAccount,
                month: props.selectedBank.month,
                year: props.selectedYear,
                file_name: file,
                folder: folder,
                tabText,
            };

            await axios
                .post("/deleteFileS3", params)
                .then(() => {
                    props.onClose();
                    props.loadInitData();
                    props.setTexto(t("dialogConversions.filesRemoved"));
                    props.setAlertType("success");
                    props.setShow(true);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
        setBackDrop(false);
    };

    const downloadFile = (file, folder) => async () => {
        let params = {};
        if (
            props.uploadedFiles.filter((item) => item.req_type.toString() === props.buttonTabs.toString())[0].files
                .length === 0
        ) {
            params = {
                id_organisation: props.selectedBank.idOrganisation,
                bank_id: props.selectedBank.idAccount,
                month: props.selectedBank.month,
                year: props.selectedYear,
                file_name: file.name,
                folder: folder,
            };
        } else {
            params = {
                id_organisation: props.selectedBank.idOrganisation,
                bank_id: props.selectedBank.idAccount,
                month: props.selectedBank.month,
                year: props.selectedYear,
                file_name: file,
                folder: folder,
            };
        }

        await axios
            .get(`/getFileS3`, { params, responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", response.config.params.file_name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const downloadAll = (folder) => async () => {
        let params = {};
        params = {
            id_organisation: props.selectedBank.idOrganisation,
            bank_id: props.selectedBank.idAccount,
            month: props.selectedBank.month,
            year: props.selectedYear,
            folder: folder,
        };

        await axios
            .get(`/getBulkFilesS3`, { params, responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                let nameZip =
                    props.selectedBank.company +
                    " - " +
                    props.selectedBank.account +
                    " - " +
                    moment(props.showTabs.current_date).format("YYYY MM") +
                    ".zip";
                link.setAttribute("download", nameZip);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const deleteAll = (folder) => async () => {
        setOpenAlertDelete(false);
        setBackDrop(true);
        let params = {};
        let tabText =
            props.buttonTabs === 1
                ? "Estados de cuenta"
                : props.buttonTabs === 2
                    ? "Cheques"
                    : props.buttonTabs === 3
                        ? "Reporte de cajas chicas"
                        : "";

        if (
            props.uploadedFiles.filter((item) => item.req_type.toString() === props.buttonTabs.toString())[0].files
                .length === 0
        ) {
            params = {
                id_organisation: props.selectedBank.idOrganisation,
                bank_id: props.selectedBank.idAccount,
                month: props.selectedBank.month,
                year: props.selectedYear,
                folder: folder,
                tabText,
            };
            await axios
                .post("/deleteBulkFilesS3", params)
                .then(() => {
                    if (folder === "Bancos") {
                        props.setFilesData([]);
                    } else if (folder === "Cheques") {
                        props.setFilesDataChecks([]);
                    } else {
                        props.setFilesDataPetty([]);
                    }
                    props.loadInitData();
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            params = {
                id_organisation: props.selectedBank.idOrganisation,
                bank_id: props.selectedBank.idAccount,
                month: props.selectedBank.month,
                year: props.selectedYear,
                folder: folder,
                tabText,
            };
            await axios
                .post("/deleteBulkFilesS3", params)
                .then(() => {
                    props.onClose();
                    props.loadInitData();
                    props.setTexto(t("dialogConversions.filesRemoved"));
                    props.setAlertType("success");
                    props.setShow(true);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
        setBackDrop(false);
    };

    const acceptedFileItems = props.filesData.map((file) => (
        <ListItem
            disablePadding
            secondaryAction={
                <Box>
                    <LightTooltip title={t("dialogConversions.download")} aria-label="downloadFile">
                        <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={downloadFile(file, "Bancos")}
                            sx={{ mr: 1 }}
                        >
                            <FileDownloadIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                    <LightTooltip title={t("dialogConversions.remove")} aria-label="removeFile">
                        <IconButton edge="end" aria-label="delete" onClick={removeFile(file, "Bancos")}>
                            <DeleteIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                </Box>
            }
            sx={{ height: 50 }}
        >
            <ListItemIcon sx={{ minWidth: "20px" }}>
                <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
            </ListItemIcon>
            <ListItemText
                key={file.path}
                primary={file.name}
                secondary={formatBytes(file.size)}
                secondaryTypographyProps={{ fontSize: "12px" }}
            />
        </ListItem>
    ));

    const acceptedFileItemsChecks = props.filesDataChecks.map((file) => (
        <ListItem
            disablePadding
            secondaryAction={
                <Box>
                    <LightTooltip title={t("dialogConversions.download")} aria-label="downloadFile">
                        <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={downloadFile(file, "Cheques")}
                            sx={{ mr: 1 }}
                        >
                            <FileDownloadIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                    <LightTooltip title={t("dialogConversions.remove")} aria-label="removeFile">
                        <IconButton edge="end" aria-label="delete" onClick={removeFile(file, "Cheques")}>
                            <DeleteIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                </Box>
            }
            sx={{ height: 50 }}
        >
            <ListItemIcon sx={{ minWidth: "20px" }}>
                <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
            </ListItemIcon>
            <ListItemText
                key={file.path}
                primary={file.name}
                secondary={formatBytes(file.size)}
                secondaryTypographyProps={{ fontSize: "12px" }}
            />
        </ListItem>
    ));

    const acceptedFileItemsPetty = props.filesDataPetty.map((file) => (
        <ListItem
            disablePadding
            secondaryAction={
                <Box>
                    <LightTooltip title={t("dialogConversions.download")} aria-label="downloadFile">
                        <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={downloadFile(file, "CajasChicas")}
                            sx={{ mr: 1 }}
                        >
                            <FileDownloadIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                    <LightTooltip title={t("dialogConversions.remove")} aria-label="removeFile">
                        <IconButton edge="end" aria-label="delete" onClick={removeFile(file, "CajasChicas")}>
                            <DeleteIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                </Box>
            }
            sx={{ height: 50 }}
        >
            <ListItemIcon sx={{ minWidth: "20px" }}>
                <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
            </ListItemIcon>
            <ListItemText
                key={file.path}
                primary={file.name}
                secondary={formatBytes(file.size)}
                secondaryTypographyProps={{ fontSize: "12px" }}
            />
        </ListItem>
    ));

    const fileRejectionItems = useMemo(
        () =>
            props.filesRejected.map(({ file }) => (
                <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                        <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
                    </ListItemIcon>
                    <ListItemText
                        key={file.path}
                        primary={file.name}
                        secondary={formatBytes(file.size)}
                        secondaryTypographyProps={{ fontSize: "12px" }}
                        sx={{ color: "#ff5968" }}
                    />
                </ListItem>
            )),
        [props.filesRejected]
    );

    const fileRejectionItemsChecks = useMemo(
        () =>
            props.filesRejectedChecks.map(({ file }) => (
                <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                        <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
                    </ListItemIcon>
                    <ListItemText
                        key={file.path}
                        primary={file.name}
                        secondary={formatBytes(file.size)}
                        secondaryTypographyProps={{ fontSize: "12px" }}
                        sx={{ color: "#ff5968" }}
                    />
                </ListItem>
            )),
        [props.filesRejectedChecks]
    );

    const fileRejectionItemsPetty = useMemo(
        () =>
            props.filesRejectedPetty.map(({ file }) => (
                <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                        <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
                    </ListItemIcon>
                    <ListItemText
                        key={file.path}
                        primary={file.name}
                        secondary={formatBytes(file.size)}
                        secondaryTypographyProps={{ fontSize: "12px" }}
                        sx={{ color: "#ff5968" }}
                    />
                </ListItem>
            )),
        [props.filesRejectedPetty]
    );

    const uploadedFilesItems = (files, sizes, upload_date, folder) => (
        <>
            {files.map((file, index) => (
                <ListItem
                    disablePadding
                    secondaryAction={
                        <Box>
                            <LightTooltip title={t("dialogConversions.download")} aria-label="downloadFile">
                                <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={downloadFile(file, folder)}
                                    sx={{
                                        mr: "-12px",
                                        ...(((props.component === "clientFilesMana" &&
                                            props.selectedBank.month === moment().subtract(1, "months").format("MM")) ||
                                            (props.component === "requirements" &&
                                                props.selectedBank.month ===
                                                moment().subtract(1, "months").format("MM")) ||
                                            (props.component === "requirements" &&
                                                teamDelete.includes(todos.userInfo.email) &&
                                                props.selectedBank.month !==
                                                moment().subtract(1, "months").format("MM"))) && {
                                            mr: 1,
                                        }),
                                    }}
                                >
                                    <FileDownloadIcon fontSize="medium" color="primary" />
                                </IconButton>
                            </LightTooltip>
                            {(props.component === "clientFilesMana" &&
                                props.selectedBank.month === moment().subtract(1, "months").format("MM")) ||
                                (props.component === "requirements" &&
                                    props.selectedBank.month === moment().subtract(1, "months").format("MM")) ||
                                (props.component === "requirements" &&
                                    teamDelete.includes(todos.userInfo.email) &&
                                    props.selectedBank.month !== moment().subtract(1, "months").format("MM")) ? (
                                <LightTooltip title={t("dialogConversions.remove")} aria-label="removeUploadedFile">
                                    <IconButton edge="end" aria-label="delete" onClick={removeFile(file, folder)}>
                                        <DeleteIcon fontSize="medium" color="primary" />
                                    </IconButton>
                                </LightTooltip>
                            ) : null}
                        </Box>
                    }
                    sx={{ height: 50 }}
                >
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                        <DescriptionOutlinedIcon fontSize="medium" color="primary" sx={{ mr: 1 }} />
                    </ListItemIcon>
                    <ListItemText
                        key={index}
                        primary={
                            <Typography>
                                {file}&nbsp;&nbsp;({sizes[index]})&nbsp;&nbsp;
                                <b>{upload_date && upload_date[index] ? "(" + moment.utc(upload_date[index]).format(todos.dateFormat) + ")" : ""}</b>
                            </Typography>
                        }
                    />
                </ListItem>
            ))}
        </>
    );

    const uploadedFilesItems2 = (files, sizes, upload_date, folder) => {
        let data = files;
        return (
            <>
                <Paper sx={{ boxShadow: 0 }}>
                    <TableContainer>
                        <Table aria-label="simple table">
                            <TableBody>
                                {data
                                    .slice(props.page * rowsPerPage, props.page * rowsPerPage + rowsPerPage)
                                    .map((file, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                        >
                                            <TableCell align="left" sx={{ p: 0, border: 0, display: "flex" }}>
                                                <DescriptionOutlinedIcon
                                                    fontSize="medium"
                                                    color="primary"
                                                    sx={{ mr: 1 }}
                                                />
                                                <Typography>
                                                    {file}&nbsp;&nbsp;({sizes[index]})&nbsp;&nbsp;
                                                    <b>{upload_date && upload_date[index] ? "(" + moment.utc(upload_date[index]).format(todos.dateFormat) + ")" : ""}</b>
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 0, border: 0, whiteSpace: "nowrap" }}>
                                                <Box>
                                                    <LightTooltip
                                                        title={t("dialogConversions.download")}
                                                        aria-label="downloadFile"
                                                    >
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="delete"
                                                            onClick={downloadFile(file, folder)}
                                                            sx={{
                                                                mr: "-12px",
                                                                ...(((props.component === "clientFilesMana" &&
                                                                    props.selectedBank.month ===
                                                                    moment().subtract(1, "months").format("MM")) ||
                                                                    (props.component === "requirements" &&
                                                                        props.selectedBank.month ===
                                                                        moment()
                                                                            .subtract(1, "months")
                                                                            .format("MM")) ||
                                                                    (props.component === "requirements" &&
                                                                        teamDelete.includes(todos.userInfo.email) &&
                                                                        props.selectedBank.month !==
                                                                        moment()
                                                                            .subtract(1, "months")
                                                                            .format("MM"))) && {
                                                                    mr: 1,
                                                                }),
                                                            }}
                                                        >
                                                            <FileDownloadIcon fontSize="medium" color="primary" />
                                                        </IconButton>
                                                    </LightTooltip>
                                                    {(props.component === "clientFilesMana" &&
                                                        props.selectedBank.month ===
                                                        moment().subtract(1, "months").format("MM")) ||
                                                        (props.component === "requirements" &&
                                                            props.selectedBank.month ===
                                                            moment().subtract(1, "months").format("MM")) ||
                                                        (props.component === "requirements" &&
                                                            teamDelete.includes(todos.userInfo.email) &&
                                                            props.selectedBank.month !==
                                                            moment().subtract(1, "months").format("MM")) ? (
                                                        <LightTooltip
                                                            title={t("dialogConversions.remove")}
                                                            aria-label="removeUploadedFile"
                                                        >
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="delete"
                                                                onClick={removeFile(file, folder)}
                                                            >
                                                                <DeleteIcon fontSize="medium" color="primary" />
                                                            </IconButton>
                                                        </LightTooltip>
                                                    ) : null}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        shape="rounded"
                        rowsPerPageOptions={[10]}
                        component="div"
                        count={data.length}
                        rowsPerPage={rowsPerPage}
                        page={props.page}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("miscellaneous.of")} ${count}`}
                    />
                </Paper>
            </>
        );
    };

    return (
        <>
            <DialogAlerts
                open={openAlertDelete}
                onClose={() => setOpenAlertDelete(false)}
                agreeBtnLabel={t("miscellaneous.accept")}
                disagreeBtnLabel={t("miscellaneous.cancel")}
                type={"info"}
                title={t("miscellaneous.filesSureDelete")}
                agreeAction={deleteAll(props.buttonTabs.toString() === "2" ? "Cheques" : "CajasChicas")}
            />
            <CustomDialog
                open={props.open}
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        props.onClose();
                    }
                }}
                maxWidth="md"
                fullWidth={true}
            >
                <DialogTitle id="alert-dialog-title">
                    {props.selectedBank.company} /{" "}
                    <span style={{ color: "#ff5968" }}>{props.selectedBank.account}</span> /{" "}
                    {meses[parseInt(props.selectedBank.month) - 1]}
                </DialogTitle>
                <DialogContent dividers sx={{ pb: props.buttonTabs === "1" ? "20px !important" : "0 !important" }}>
                    <StyledToggleButtonGroup
                        color="primary"
                        value={props.buttonTabs}
                        exclusive
                        onChange={props.handleChangeTabs}
                        sx={{ pb: 4 }}
                    >
                        {props.showTabs.requirement_bank_statements &&
                            moment(props.showTabs.current_date).format("YYYY-MM-DD") >=
                            moment.utc(props.showTabs.initial_date).format("YYYY-MM-DD") && (
                                <ToggleButton value="1" sx={{ py: 3, width: "100% !important" }}>
                                    {t("miscellaneous.bankStatements")}
                                </ToggleButton>
                            )}
                        {props.showTabs.requirement_checks &&
                            !props.showTabs.included_checks &&
                            moment(props.showTabs.current_date).format("YYYY-MM-DD") >=
                            moment.utc(props.showTabs.initial_date).format("YYYY-MM-DD") && (
                                <ToggleButton value="2" sx={{ py: 3, width: "100% !important" }}>
                                    {t("services.checks")}
                                </ToggleButton>
                            )}
                        {props.showTabs.requirement_petty_cash &&
                            moment(props.showTabs.current_date).format("YYYY-MM-DD") >=
                            moment.utc(props.showTabs.initial_date).format("YYYY-MM-DD") && (
                                <ToggleButton value="3" sx={{ py: 3, width: "100% !important" }}>
                                    {t("miscellaneous.pettyCash")}
                                </ToggleButton>
                            )}
                    </StyledToggleButtonGroup>
                    {props.buttonTabs === "1" && (
                        <>
                            <div
                                {...getRootBanksProps({
                                    className:
                                        props.filesData.length > 0 ||
                                            (props.uploadedFiles.length > 0 &&
                                                props.uploadedFiles.filter(
                                                    (item) => item.req_type.toString() === props.buttonTabs.toString()
                                                )[0].files.length > 0) ||
                                            props.fileFormat.length === 0 ||
                                            props.checkStatus
                                            ? classes.dropzoneDisabled
                                            : classes.dropzone,
                                })}
                            >
                                <input {...getInputBanksProps()} />
                                {props.fileFormat.length > 0 ? (
                                    <CloudUploadIcon
                                        fill={
                                            props.filesData.length > 0 ||
                                                (props.uploadedFiles.length > 0 &&
                                                    props.uploadedFiles.filter(
                                                        (item) => item.req_type.toString() === props.buttonTabs.toString()
                                                    )[0].files.length > 0) ||
                                                props.checkStatus
                                                ? "#C1C1DE"
                                                : "#031851"
                                        }
                                    />
                                ) : (
                                    <InfoIcon height={100} fill={"#C1C1DE"} />
                                )}
                                {props.fileFormat.length > 0 ? (
                                    <Typography
                                        variant="h1"
                                        color={
                                            props.filesData.length > 0 ||
                                                (props.uploadedFiles.length > 0 &&
                                                    props.uploadedFiles.filter(
                                                        (item) => item.req_type.toString() === props.buttonTabs.toString()
                                                    )[0].files.length > 0) ||
                                                props.checkStatus
                                                ? "#C1C1DE"
                                                : "#031851"
                                        }
                                        sx={{ cursor: "default", pt: 3 }}
                                    >
                                        {t("dialogConversions.selectFile")}
                                    </Typography>
                                ) : (
                                    <Typography variant="h1" color={"#C1C1DE"} sx={{ cursor: "default", pt: 3 }}>
                                        {t("dialogConversions.noBanks")}
                                    </Typography>
                                )}
                            </div>
                            {props.fileFormat.length > 0 && (
                                <Typography variant="body1" color="primary" sx={{ pt: 1 }}>
                                    {t("dialogConversions.validFormats")}{" "}
                                    <Typography component="span" variant="h4" color="primary">{`${props.fileFormat
                                        .map((file) => `.${file}`)
                                        .join(" ")}`}</Typography>
                                    <Typography variant="body1" color="primary">
                                        {t("dialogConversions.maximunUploadSize")}
                                    </Typography>
                                </Typography>
                            )}
                            {progress > 0 && <LinearProgressWithLabel value={progress} />}
                            {acceptedFileItems.length > 0 && (
                                <List
                                    component="nav"
                                    aria-labelledby="acceptedFiles-list-subheader"
                                    subheader={
                                        <ListSubheader component="div" id="acceptedFiles-list-subheader" sx={{ p: 0 }}>
                                            <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                {`${t("dialogConversions.uploadedFiles")}:`}
                                            </Typography>
                                        </ListSubheader>
                                    }
                                >
                                    {acceptedFileItems}
                                </List>
                            )}
                            {fileRejectionItems.length > 0 && (
                                <List
                                    component="nav"
                                    aria-labelledby="fileRejections-list-subheader"
                                    subheader={
                                        <ListSubheader component="div" id="fileRejections-list-subheader" sx={{ p: 0 }}>
                                            <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                {`${t("dialogConversions.rejectedFiles")}:`}
                                            </Typography>
                                        </ListSubheader>
                                    }
                                >
                                    {fileRejectionItems}
                                </List>
                            )}
                            {props.uploadedFiles.length > 0 &&
                                props.uploadedFiles.filter(
                                    (item) => item.req_type.toString() === props.buttonTabs.toString()
                                )[0].files.length > 0 && (
                                    <List
                                        component="nav"
                                        aria-labelledby="uploadedFiles-list-subheader"
                                        subheader={
                                            <ListSubheader
                                                component="div"
                                                id="uploadedFiles-list-subheader"
                                                sx={{ p: 0 }}
                                            >
                                                <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                    {`${t("dialogConversions.repositoryFiles")}:`}
                                                </Typography>
                                            </ListSubheader>
                                        }
                                    >
                                        {uploadedFilesItems(
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0].files,
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0].sizes,
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0]?.upload_date,
                                            "Bancos"
                                        )}
                                    </List>
                                )}
                            {(props.selectedBank.showCheck !== 1 || props.selectedBank.NotApply) &&
                                props.filesData.length +
                                (props.uploadedFiles.length > 0 &&
                                    props.uploadedFiles.filter(
                                        (item) => item.req_type.toString() === props.buttonTabs.toString()
                                    )[0].files.length) ===
                                0 && (
                                    <CustomFormControlLabel
                                        control={
                                            <Checkbox
                                                checked={props.checkStatus}
                                                onChange={(e) =>
                                                    props.handleChangeStatus(e, props.buttonTabs.toString())
                                                }
                                            />
                                        }
                                        label={t("miscellaneous.notApplyMonth")}
                                    />
                                )}
                        </>
                    )}
                    {props.buttonTabs === "2" && props.showTabs.requirement_checks && (
                        <>
                            <div
                                {...getRootChecksProps({
                                    className: props.checkStatusChecks || props.fullLoadChecks ? classes.dropzoneDisabled : classes.dropzone,
                                })}
                            >
                                <input {...getInputChecksProps()} />
                                <CloudUploadIcon fill={props.checkStatusChecks || props.fullLoadChecks ? "#C1C1DE" : "#031851"} />
                                <Typography
                                    variant="h1"
                                    color={props.checkStatusChecks || props.fullLoadChecks ? "#C1C1DE" : "#031851"}
                                    sx={{ cursor: "default", pt: 3 }}
                                >
                                    {t("dialogConversions.selectFile")}
                                </Typography>
                            </div>
                            <Typography variant="body1" color="primary" sx={{ pt: 1 }}>
                                {t("dialogConversions.validFormats")}{" "}
                                <Typography component="span" variant="h4" color="primary">
                                    .pdf, .jpg, .jpeg, .png, .xlsx, .zip
                                </Typography>
                                <Typography variant="body1" color="primary">
                                    {t("dialogConversions.monthlyUploadLimit")} 10 MB.
                                </Typography>
                            </Typography>
                            {progressChecks > 0 && <LinearProgressWithLabel value={progressChecks} />}
                            <CustomFormControlLabel
                                control={
                                    <Checkbox
                                        checked={props.fullLoadChecks}
                                        onChange={(e) =>
                                            props.handleChangeFullLoadChecks()
                                        }
                                        disabled={props.checkStatusChecks || props.filesDataChecks.length +
                                            (props.uploadedFiles.length > 0 &&
                                                props.uploadedFiles.filter(
                                                    (item) => item.req_type.toString() === props.buttonTabs.toString()
                                                )[0].files.length) ===
                                            0}
                                    />
                                }
                                sx={{
                                    pb: props.filesDataChecks.length +
                                        (props.uploadedFiles.length > 0 &&
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0].files.length) ===
                                        0 ? "32px" : 0,
                                    pt: props.filesDataChecks.length +
                                        (props.uploadedFiles.length > 0 &&
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0].files.length) ===
                                        0 ? 0 : 2,
                                }}
                                label={t("miscellaneous.fullLoad")}
                            />
                            {props.filesDataChecks.length +
                                (props.uploadedFiles.length > 0 &&
                                    props.uploadedFiles.filter(
                                        (item) => item.req_type.toString() === props.buttonTabs.toString()
                                    )[0].files.length) ===
                                0 && (
                                    <CustomFormControlLabel
                                        control={
                                            <Checkbox
                                                checked={props.checkStatusChecks}
                                                onChange={(e) => props.handleChangeStatus(e, props.buttonTabs.toString())}
                                            />
                                        }
                                        sx={{ pb: "32px" }}
                                        label={t("miscellaneous.notApplyMonth")}
                                    />
                                )}
                            {acceptedFileItemsChecks.length > 0 && (
                                <List
                                    component="nav"
                                    aria-labelledby="acceptedFiles-list-subheader"
                                    subheader={
                                        <ListSubheader component="div" id="acceptedFiles-list-subheader" sx={{ p: 0 }}>
                                            <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                {`${t("dialogConversions.uploadedFiles")}:`}
                                            </Typography>
                                        </ListSubheader>
                                    }
                                >
                                    {acceptedFileItemsChecks}
                                </List>
                            )}
                            {fileRejectionItemsChecks.length > 0 && (
                                <List
                                    component="nav"
                                    aria-labelledby="fileRejections-list-subheader"
                                    subheader={
                                        <ListSubheader component="div" id="fileRejections-list-subheader" sx={{ p: 0 }}>
                                            <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                {`${t("dialogConversions.rejectedFiles")}:`}
                                            </Typography>
                                        </ListSubheader>
                                    }
                                >
                                    {fileRejectionItemsChecks}
                                </List>
                            )}
                            {props.uploadedFiles.length > 0 &&
                                props.uploadedFiles.filter(
                                    (item) => item.req_type.toString() === props.buttonTabs.toString()
                                )[0].files.length > 0 && (
                                    <List
                                        component="nav"
                                        aria-labelledby="uploadedFiles-list-subheader"
                                        subheader={
                                            <>
                                                <ListItem
                                                    disablePadding
                                                    secondaryAction={
                                                        props.uploadedFiles.length > 0 &&
                                                        props.uploadedFiles.filter(
                                                            (item) =>
                                                                item.req_type.toString() === props.buttonTabs.toString()
                                                        )[0].files.length > 1 && (
                                                            <Box>
                                                                <LightTooltip
                                                                    title={t("dialogConversions.download")}
                                                                    aria-label="downloadFile"
                                                                >
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="download"
                                                                        onClick={downloadAll("Cheques")}
                                                                        sx={{ mr: 1 }}
                                                                    >
                                                                        <FileDownloadIcon
                                                                            fontSize="medium"
                                                                            color="primary"
                                                                        />
                                                                    </IconButton>
                                                                </LightTooltip>
                                                                {(props.component === "clientFilesMana" &&
                                                                    props.selectedBank.month ===
                                                                    moment().subtract(1, "months").format("MM")) ||
                                                                    (props.component === "requirements" &&
                                                                        props.selectedBank.month ===
                                                                        moment().subtract(1, "months").format("MM")) ||
                                                                    (props.component === "requirements" &&
                                                                        teamDelete.includes(todos.userInfo.email) &&
                                                                        props.selectedBank.month !==
                                                                        moment().subtract(1, "months").format("MM")) ? (
                                                                    <LightTooltip
                                                                        title={t("dialogConversions.remove")}
                                                                        aria-label="removeFile"
                                                                    >
                                                                        <IconButton
                                                                            edge="end"
                                                                            aria-label="delete"
                                                                            onClick={() => setOpenAlertDelete(true)}
                                                                        >
                                                                            <DeleteIcon
                                                                                fontSize="medium"
                                                                                color="primary"
                                                                            />
                                                                        </IconButton>
                                                                    </LightTooltip>
                                                                ) : null}
                                                            </Box>
                                                        )
                                                    }
                                                    sx={{ height: 50 }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="h4"
                                                                color="primary"
                                                                sx={{ pt: 3, pb: 1 }}
                                                            >
                                                                {`${t("dialogConversions.repositoryFiles")} (${props.uploadedFiles.filter(
                                                                    (item) =>
                                                                        item.req_type.toString() ===
                                                                        props.buttonTabs.toString()
                                                                )[0].files.length
                                                                    }):`}
                                                            </Typography>
                                                        }
                                                        secondaryTypographyProps={{ fontSize: "12px" }}
                                                    />
                                                </ListItem>

                                                <Divider sx={{ py: 0.5 }} />
                                            </>
                                        }
                                    >
                                        {uploadedFilesItems2(
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0].files,
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0].sizes,
                                            props.uploadedFiles.filter(
                                                (item) => item.req_type.toString() === props.buttonTabs.toString()
                                            )[0]?.upload_date,
                                            "Cheques"
                                        )}
                                    </List>
                                )}
                        </>
                    )}
                    {props.buttonTabs === "3" && props.showTabs.requirement_petty_cash && (
                        <>
                            <div
                                {...getRootPettyProps({
                                    className: props.checkStatusPetty ? classes.dropzoneDisabled : classes.dropzone,
                                })}
                            >
                                <input {...getInputPettyProps()} />
                                <CloudUploadIcon fill={props.checkStatusPetty ? "#C1C1DE" : "#031851"} />
                                <Typography
                                    variant="h1"
                                    color={props.checkStatusPetty ? "#C1C1DE" : "#031851"}
                                    sx={{ cursor: "default", pt: 3 }}
                                >
                                    {t("dialogConversions.selectFile")}
                                </Typography>
                            </div>
                            <Typography variant="body1" color="primary" sx={{ pt: 1 }}>
                                {t("dialogConversions.validFormats")}{" "}
                                <Typography component="span" variant="h4" color="primary">
                                    .pdf, .xlsx
                                </Typography>
                                <Typography variant="body1" color="primary">
                                    {t("dialogConversions.monthlyUploadLimit")} 5 MB.
                                </Typography>
                            </Typography>
                            {progressPetty > 0 && <LinearProgressWithLabel value={progressPetty} />}
                            {acceptedFileItemsPetty.length > 0 ? (
                                <List
                                    component="nav"
                                    aria-labelledby="acceptedFiles-list-subheader"
                                    subheader={
                                        <ListSubheader component="div" id="acceptedFiles-list-subheader" sx={{ p: 0 }}>
                                            <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                {`${t("dialogConversions.uploadedFiles")}:`}
                                            </Typography>
                                        </ListSubheader>
                                    }
                                >
                                    {acceptedFileItemsPetty}
                                </List>
                            ) : null}
                            {fileRejectionItemsPetty.length > 0 ? (
                                <List
                                    component="nav"
                                    aria-labelledby="fileRejections-list-subheader"
                                    subheader={
                                        <ListSubheader component="div" id="fileRejections-list-subheader" sx={{ p: 0 }}>
                                            <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                {`${t("dialogConversions.rejectedFiles")}:`}
                                            </Typography>
                                        </ListSubheader>
                                    }
                                >
                                    {fileRejectionItemsPetty}
                                </List>
                            ) : null}
                            {props.uploadedFiles.length > 0 &&
                                props.uploadedFiles.filter(
                                    (item) => item.req_type.toString() === props.buttonTabs.toString()
                                )[0].files.length > 0 ? (
                                <List
                                    component="nav"
                                    aria-labelledby="uploadedFiles-list-subheader"
                                    subheader={
                                        <>
                                            <ListItem
                                                disablePadding
                                                secondaryAction={
                                                    props.uploadedFiles.length > 0 &&
                                                    props.uploadedFiles.filter(
                                                        (item) =>
                                                            item.req_type.toString() === props.buttonTabs.toString()
                                                    )[0].files.length > 1 && (
                                                        <Box>
                                                            <LightTooltip
                                                                title={t("dialogConversions.download")}
                                                                aria-label="downloadFile"
                                                            >
                                                                <IconButton
                                                                    edge="end"
                                                                    aria-label="delete"
                                                                    onClick={downloadAll("CajasChicas")}
                                                                    sx={{ mr: 1 }}
                                                                >
                                                                    <FileDownloadIcon
                                                                        fontSize="medium"
                                                                        color="primary"
                                                                    />
                                                                </IconButton>
                                                            </LightTooltip>
                                                            {(props.component === "clientFilesMana" &&
                                                                props.selectedBank.month ===
                                                                moment().subtract(1, "months").format("MM")) ||
                                                                (props.component === "requirements" &&
                                                                    props.selectedBank.month ===
                                                                    moment().subtract(1, "months").format("MM")) ||
                                                                (props.component === "requirements" &&
                                                                    teamDelete.includes(todos.userInfo.email) &&
                                                                    props.selectedBank.month !==
                                                                    moment().subtract(1, "months").format("MM")) ? (
                                                                <LightTooltip
                                                                    title={t("dialogConversions.remove")}
                                                                    aria-label="removeFile"
                                                                >
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="delete"
                                                                        onClick={() => setOpenAlertDelete(true)}
                                                                    >
                                                                        <DeleteIcon fontSize="medium" color="primary" />
                                                                    </IconButton>
                                                                </LightTooltip>
                                                            ) : null}
                                                        </Box>
                                                    )
                                                }
                                                sx={{ height: 50 }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                            {`${t("dialogConversions.repositoryFiles")} (${props.uploadedFiles.filter(
                                                                (item) =>
                                                                    item.req_type.toString() ===
                                                                    props.buttonTabs.toString()
                                                            )[0].files.length
                                                                }):`}
                                                        </Typography>
                                                    }
                                                    secondaryTypographyProps={{ fontSize: "12px" }}
                                                />
                                            </ListItem>

                                            <Divider sx={{ py: 0.5 }} />
                                        </>
                                    }
                                >
                                    {uploadedFilesItems2(
                                        props.uploadedFiles.filter(
                                            (item) => item.req_type.toString() === props.buttonTabs.toString()
                                        )[0].files,
                                        props.uploadedFiles.filter(
                                            (item) => item.req_type.toString() === props.buttonTabs.toString()
                                        )[0].sizes,
                                        props.uploadedFiles.filter(
                                            (item) => item.req_type.toString() === props.buttonTabs.toString()
                                        )[0]?.upload_date,
                                        "CajasChicas"
                                    )}
                                </List>
                            ) : null}
                            {props.filesDataPetty.length +
                                (props.uploadedFiles.length > 0 &&
                                    props.uploadedFiles.filter(
                                        (item) => item.req_type.toString() === props.buttonTabs.toString()
                                    )[0].files.length) ===
                                0 && (
                                    <CustomFormControlLabel
                                        control={
                                            <Checkbox
                                                checked={props.checkStatusPetty}
                                                onChange={(e) => props.handleChangeStatus(e, props.buttonTabs.toString())}
                                            />
                                        }
                                        sx={{ pb: "32px" }}
                                        label={t("miscellaneous.notApplyMonth")}
                                    />
                                )}
                        </>
                    )}
                    {props.buttonTabs === "3" && !props.showTabs.requirement_petty_cash && (
                        <Typography variant="h5" color="primary" sx={{ pb: 8, textAlign: "center" }}>
                            {t("principal.noReqConfig")}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: "16px 32px !important" }}>{props.actions}</DialogActions>
                <Backdrop open={showBackdrop} />
            </CustomDialog>
        </>
    );
};

export default UploadAsync;
